import { useEffect, useRef } from 'react';
import GLB_object from '../Components/GLBobject';
import RT3DControls from '../three-controls/RT3DControls';
import * as THREE from 'three';
import {
	DEFAULT_WALK_VELOCITY,
	DEFAULT_FPS_MAX_DISTANCE_FROM_CAMERA,
} from '../Avatars/AvatarControls/Constants';
import PropTypes from 'prop-types';

import { getDistanceXInCameraDirection } from '@3jss/utils/cameraHelpers';
import { CONTROL_MODES } from '@3jss/utils/constants';

let meshesToHide = [
	'entry_area',
	'kate_area',
	'lily_area',
	'castle_area',
	'collision_mesh',
	'Navigable_Mesh',
];

const KEYCODE = {
	W: 87,
	A: 65,
	S: 83,
	D: 68,
	ARROW_LEFT: 37,
	ARROW_UP: 38,
	ARROW_RIGHT: 39,
	ARROW_DOWN: 40,
};

/**
 *
 * @module SceneModelObject
 * @name SceneModelObject
 *
 * @desc
 * A React functional component to render the scene model in realtime 3D Scenes

 */
const SceneModelObject = ({
	scene_model,
	scene,
	onBackgroundReady,
	envMapIntensity = 1.0,
	controller,
	camera,
	renderer,
	RT3DControlsRef,
	orbitControlsConfig,
	controlMode = CONTROL_MODES.AVATAR_NAVIGATION,
	hasAvatar = false,
	walkVelocity = DEFAULT_WALK_VELOCITY,
	isXREnabled = false,
	setRT3DControlsReady,
	rotateCameraTowardsMovementDirection,
	setCurrentCamera,
}) => {
	const _3dModelRef = useRef();

	useEffect(() => {
		window.addEventListener(
			'scene_toggle_mesh_visibility',
			setMeshVisibility,
		);

		window.addEventListener(
			'set_scene_model_KHR_variant',
			setSceneModelKHRVariant,
		);
		return () => {
			window.removeEventListener(
				'set_scene_model_KHR_variant',
				setSceneModelKHRVariant,
			);

			window.removeEventListener(
				'scene_toggle_mesh_visibility',
				setMeshVisibility,
			);
		};
	}, []);

	const setSceneModelKHRVariant = (e) => {
		const variant = e.detail.variant;

		_3dModelRef.current.set_KHR_Variant(variant, false);
	};

	const setMeshVisibility = ({ detail }) => {
		const { meshName, visible } = detail;
		let mesh = scene.getObjectByName(meshName);
		if (mesh) {
			mesh.visible = visible;
		}

		RT3DControlsRef.current?.updateSceneCollider();
	};

	/**
	 * @function onSceneUrlChanged
	 *
	 * @desc
	 * Triggered every time the scene_model.url changes.
	 */
	useEffect(() => {
		if (scene_model?.url) {
			_3dModelRef.current = new GLB_object({
				name: 'scene_model',
				useBoxHelper: false,
				is_animation_on: true,
				onLoad: onSceneModelLoaded,
				environment_map_intensity: envMapIntensity,
				scene: scene,
				isXREnabled: isXREnabled,
				renderer: renderer,
				camera: camera,
				setCurrentCamera: setCurrentCamera,
				isScrollToMove: controlMode === CONTROL_MODES.SCROLL,
				orbitControlsConfig,
				...scene_model,
			});
			_3dModelRef.current._type = 'scene_3d_model';
			_3dModelRef.current.userData._id = 'scene_model';
			scene.add(_3dModelRef.current);
		}
		return () => {
			if (_3dModelRef?.current?.dispose) _3dModelRef.current.dispose();
			if (RT3DControlsRef?.current?.dispose)
				RT3DControlsRef.current.dispose();
			scene.remove(_3dModelRef.current);
		};
	}, [scene_model?.url, isXREnabled]);

	const onSceneModelLoaded = () => {
		meshesToHide.forEach((meshName) => {
			let mesh = scene.getObjectByName(meshName);
			if (mesh) {
				mesh.visible = false;
			}
		});

		setupControls();
		onBackgroundReady && onBackgroundReady(true);
	};

	/**
	 * @function setupControls
	 * @desc
	 * Instantiates and configures {@link RT3DControls} for Realtime 3D Scenes
	 */
	const setupControls = () => {
		// TODO: Investigate why is this needed
		// controller.reset();
		// controller.enabled = false;
		// controller.updateFromConfig(orbitControlsConfig);
		// controller.update();
		// controller.enabled = true;

		const sceneModel = scene.children.find(
			(item) => item?._type === 'scene_3d_model',
		);

		if (controlMode === CONTROL_MODES.SCROLL) return;

		if (
			controlMode === CONTROL_MODES.AVATAR_NAVIGATION ||
			controlMode === CONTROL_MODES.FIRST_PERSON
		) {
			RT3DControlsRef.current = new RT3DControls(
				controller,
				camera,
				scene,
				sceneModel,
				walkVelocity,
				orbitControlsConfig,
				renderer,
				controlMode,
				scene_model,
				rotateCameraTowardsMovementDirection
			);
			setRT3DControlsReady(true);

			if (controlMode === CONTROL_MODES.FIRST_PERSON) {
				setupDummyTarget();
			}
		}
	};

	const setupDummyTarget = () => {
		const mesh = new THREE.Mesh(
			new THREE.BoxGeometry(0.6, 1.5, 0.6, 1),
			new THREE.MeshBasicMaterial({ color: 0xff0000 }),
		);

		mesh.geometry.translate(0, 1, 0);

		// Use raycaster to find the distance between the controller target and the scene model. This is done to preserve the camera angle set from AVA.
		// Init Raycaster with the controller target and a direction vector pointing downwards.
		const raycaster = new THREE.Raycaster(
			controller.target,
			new THREE.Vector3(0, -1, 0),
			0,
			1000,
		);
		raycaster.camera = camera;
		// Find the closest intersection point between the ray and the scene model.
		const sceneModel = scene.children.find(
			(item) => item?._type === 'scene_3d_model',
		);
		const closest = raycaster
			.intersectObject(sceneModel)
			.reduce((acc, curr) => {
				if (acc.distance < curr.distance) {
					return acc;
				}
				return curr;
			});

		// Set the yOffset of the RT3DControls to the distance between the controller target and the scene model.
		RT3DControlsRef.current.yOffset = closest.distance;

		mesh.position.copy(
			getDistanceXInCameraDirection(
				camera,
				DEFAULT_FPS_MAX_DISTANCE_FROM_CAMERA,
				_3dModelRef.current.position.y + 0.1,
			),
		);
		// scene.add(mesh);
		RT3DControlsRef.current.connectTargetToControls(mesh);
		setRT3DControlsReady(true);
	};

	return false;
};

/**
 * @typedef props
 *
 * @property {Object} scene_model
 * @property {String} scene_model.url
 * @property {THREE.Scene} scene
 * @property {Function} onBackgroundReady
 * @property {Function} onBackgroundLoaded
 * @property {Number} envMapIntensity
 * @property {OrbitControls} controller
 * @property {THREE.PerspectiveCamera} camera
 * @property {THREE.WebGLRenderer} renderer
 * @property {React.Ref} RT3DControlsRef
 * @property {Number} walkVelocity
 * @property {String} controlMode - 'tpv' | 'fpv' | 'click-to-navigate'
 * @property {Boolean} hasAvatar - Whether the scene has an avatar or not.
 */
SceneModelObject.propTypes = {
	scene_model: PropTypes.object,
	scene: PropTypes.object,
	onBackgroundReady: PropTypes.func,
	onBackgroundLoaded: PropTypes.func,
	envMapIntensity: PropTypes.number,
	controller: PropTypes.object,
	camera: PropTypes.object,
	renderer: PropTypes.object,
	RT3DControlsRef: PropTypes.object,
	walkVelocity: PropTypes.number,
	controlMode: PropTypes.oneOf(['tpv', 'fpv', 'click-to-navigate']),
	hasAvatar: PropTypes.bool,
};
export default SceneModelObject;
